import { CurrencyView } from '../currency-view';
import { SpanLoading } from '../fetch-status/span-loading';
import { TextTooltip } from '../text-tooltip';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ViewChevronDownIcon } from '@/client/assets';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/client/components/ui/dropdown-menu';
import { IS_DEBUG } from '@/client/confs/app';
import { TradeParams, useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { publicClient } from '@/client/utils/chain-client';
import { getOptions, getTokenOutAmount } from '@/libs/sdk/contracts';
import { TooltipArrow } from '@radix-ui/react-tooltip';
import { MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Address } from 'viem';

type OptionPosition = TradeParams & {
  label: string;
  value: number | string;
  decimal: number;
};

/**
 * Determine if option is "Yes" type based on label
 * Color rule: Yes = green (beneficial), No = red (destructive)
 */
const isYesOption = (label: string): boolean => {
  const lowerLabel = label.toLowerCase();
  return lowerLabel === 'yes' || lowerLabel.includes('yes');
};

// Yes-side detection that also handles custom binary names (Up/Down, team
// names). CTF option addresses encode the outcome index in the last hex char,
// and index 0 is the yes-side — so the badge colors green/red correctly even
// when the label isn't literally "Yes"/"No".
const isBinaryYesSide = (label: string, optionAddress?: string): boolean => {
  if (isYesOption(label)) return true;
  const idx = optionAddress ? parseInt(optionAddress.slice(-1), 16) : NaN;
  return idx === 0;
};

type OptionsPositionBadgeProps = {
  className?: string;
  follow?: boolean;
  options: OptionPosition[];
  onClick?: (e: MouseEvent, option: OptionPosition) => void;
};

export const OptionsPositionBadge = ({ className, options, follow = true, onClick }: OptionsPositionBadgeProps) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [current, setCurrent] = useState<OptionPosition | undefined>();
  const setTradeParams = useTradeParamsStore(state => state.setTradeParams);
  const handleClick = async (e: MouseEvent, option: OptionPosition) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrent(option);
    if (follow) {
      setIsLoading(true);
      let amount = BigInt(option.value);
      try {
        if (option.type == 'buy') {
          const address = option.marketAddress as Address;
          const options = await getOptions(address, publicClient);
          const optionIndex = options.findIndex(item => item.toLowerCase() == option.optionAddress?.toLowerCase());
          const optionAmount = BigInt(option.value);
          amount = await getTokenOutAmount(
            {
              address: option.marketAddress as Address, // contract address
              optionsLength: options.length, // options length
              optionIndex: BigInt(optionIndex), // option Out Index
              optionAmount, // option Amount
            },
            publicClient
          );
        }
      } catch (error) {
        amount = BigInt(0);
        if (IS_DEBUG) console.log('filed to get amount', error);
      }
      setTradeParams({
        type: option.type,
        amount,
        marketAddress: option.marketAddress,
        optionAddress: option.optionAddress,
      });
      setIsLoading(false);
    }
    onClick?.(e, option);
    setCurrent(undefined);
  };
  if (options.length === 0) return null;
  const fast = options[0];
  if (options.length === 1) {
    const isYes = isBinaryYesSide(fast.label, fast.optionAddress);
    return (
      <TextTooltip
        show={follow}
        tooltip={follow ? t('follow-trade') : ''}
        type="button"
        disabled={isLoading}
        onClick={e => handleClick(e, fast)}
        className={cn(
          'px-1 gap-1 text-sm leading-5 rounded-1.5 font-semibold flex items-center justify-between',
          isYes ? 'bg-beneficial/20 text-beneficial-foreground' : 'bg-destructive/30 text-destructive',
          follow && isYes && 'hover:bg-beneficial hover:text-white',
          follow && !isYes && 'hover:bg-destructive hover:text-white',
          className
        )}
      >
        <CurrencyView isLoading={isLoading} value={fast.value} decimal={fast.decimal} />
        <span className="line-clamp-1 flex-1 text-start">{fast.label}</span>
      </TextTooltip>
    );
  }
  const isYes = isBinaryYesSide(fast.label, fast.optionAddress);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'gap-1 px-1 flex items-center text-sm leading-5 rounded-1.5 font-semibold flex items-center justify-between',
          isYes ? 'bg-beneficial/20 text-beneficial-foreground' : 'bg-destructive/30 text-destructive',
          follow && isYes && 'hover:bg-beneficial hover:text-white ',
          follow && !isYes && 'hover:bg-destructive hover:text-white ',
          className
        )}
      >
        <CurrencyView isLoading={isLoading} value={fast.value} decimal={fast.decimal} />
        <span className="line-clamp-1 flex-1 text-start">{fast.label}</span>
        <ViewChevronDownIcon className="size-3 fill-current" />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="rounded-4 p-2 text-sm leading-5 font-medium max-h-60 overflow-y-auto w-full max-w-62 z-10 bg-card">
        {options.map(item => {
          const itemIsYes = isYesOption(item.label);
          return (
            <Tooltip key={`options-${item.label}-${item.value}`}>
              <TooltipTrigger asChild>
                <DropdownMenuItem
                  disabled={isLoading}
                  onClick={e => handleClick(e, item)}
                  className={cn(
                    'gap-3 !rounded-3 flex p-2 h-auto items-center justify-between',
                    follow && 'cursor-pointer'
                  )}
                >
                  {current?.optionAddress == item.optionAddress && isLoading && <SpanLoading />}
                  <span className="line-clamp-1 text-start">{item.label}</span>
                  <div
                    className={cn(
                      'p-1 rounded-1.5 hover:text-white',
                      itemIsYes
                        ? 'bg-beneficial/20 text-beneficial-foreground'
                        : 'bg-destructive/30 text-destructive',
                      follow && itemIsYes && 'hover:bg-beneficial hover:text-white ',
                      follow && !itemIsYes && 'hover:bg-destructive hover:text-white ',
                      className
                    )}
                  >
                    <CurrencyView value={item.value} decimal={fast.decimal} />
                  </div>
                </DropdownMenuItem>
              </TooltipTrigger>
              {follow && (
                <TooltipContent side="right" className="text-xs font-medium leading-5 px-2.5 h-8 !animate-none">
                  {t('follow-trade')}
                  <TooltipArrow className="!animate-none fill-foreground" />
                </TooltipContent>
              )}
            </Tooltip>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
